import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Add, Delete, Edit, EditLocation, Home, Map, Room } from "@material-ui/icons"
import React, { useRef, useState } from "react"
import SelecaoEnderecoMapa, { googleMapsCarregado } from "../../../components/SelecaoEnderecoMapa"
import useAppStore from "../../../stores/AppStore"
import { useSnackbar } from "../../../utils/notificacoes"
import { useEdicaoClienteContext } from "../context/useEdicaoClienteContext"
import type { EnderecoCliente } from "../models/edicaoClienteModel"
import { useCardStyles } from "./estilosCards"

const useStyles = makeStyles({
    celulaTruncada: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    semResultado: {
        textAlign: "center",
        color: "#7f8c8d",
    },
})

type FormularioEndereco = {
    logradouro: string
    numero: string
    complemento: string
    referencia: string
    bairro: string
    cidade: string
    uf: string
    cep: string
}

type Ponto = { lat: number | null; lng: number | null }

// Endereco escolhido no SelecaoEnderecoMapa (busca ou clique no mapa)
type EnderecoGoogle = {
    rua?: string
    numero?: string
    bairro?: string
    cidade?: string
    uf?: string
    cep?: string
    lat?: number
    lng?: number
    formatted?: string
}

const pontoVazio: Ponto = { lat: null, lng: null }

const formularioVazio: FormularioEndereco = {
    logradouro: "",
    numero: "",
    complemento: "",
    referencia: "",
    bairro: "",
    cidade: "",
    uf: "",
    cep: "",
}

function paraFormulario(endereco: EnderecoCliente): FormularioEndereco {
    return {
        logradouro: endereco.logradouro ?? "",
        numero: endereco.numero ?? "",
        complemento: endereco.complemento ?? "",
        referencia: endereco.referencia ?? "",
        bairro: endereco.bairro ?? "",
        cidade: endereco.cidade ?? "",
        uf: endereco.uf ?? "",
        cep: endereco.cep ?? "",
    }
}

function resumoEndereco(f: FormularioEndereco): string {
    const rua = [f.logradouro, f.numero].filter(Boolean).join(", ")
    const local = [f.bairro, [f.cidade, f.uf].filter(Boolean).join(" - ")].filter(Boolean)
    return [rua, ...local].filter(Boolean).join(" - ")
}

export function EnderecosCliente() {
    const classes = useCardStyles()
    const tabela = useStyles()
    const { dados, salvarEndereco, excluirEndereco, adicionarEndereco } = useEdicaoClienteContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const configuracoes = useAppStore(s => s.configuracoes)
    const estabelecimento = configuracoes?.user?.parametros?.estabelecimento
    const centroLoja =
        Number(estabelecimento?.latitude) && Number(estabelecimento?.longitude)
            ? { lat: Number(estabelecimento.latitude), lng: Number(estabelecimento.longitude) }
            : null

    // Dialogo unico para novo e edicao. Com o Google Maps carregado, abre primeiro na
    // selecao pelo mapa (etapa "mapa") e depois nos dados (etapa "dados").
    const [modo, setModo] = useState<"novo" | "editar" | null>(null)
    const [emEdicao, setEmEdicao] = useState<EnderecoCliente | null>(null)
    const [etapa, setEtapa] = useState<"mapa" | "dados">("mapa")
    const [formulario, setFormulario] = useState<FormularioEndereco>(formularioVazio)
    const [ponto, setPonto] = useState<Ponto>(pontoVazio)
    const [pontoAlterado, setPontoAlterado] = useState(false)
    const [resumo, setResumo] = useState("")
    const [buscandoPonto, setBuscandoPonto] = useState(false)
    const [destacarNumero, setDestacarNumero] = useState(false)
    const [chaveBusca, setChaveBusca] = useState(0)
    const buscaRef = useRef<HTMLInputElement>(null)
    const [paraExcluir, setParaExcluir] = useState<EnderecoCliente | null>(null)

    const usarMapa = googleMapsCarregado()

    const iniciarDialogo = () => {
        setPontoAlterado(false)
        setBuscandoPonto(false)
        setDestacarNumero(false)
        setChaveBusca(k => k + 1)
        setEtapa(usarMapa ? "mapa" : "dados")
        setTimeout(() => buscaRef.current?.focus(), 300)
    }

    const abrirNovo = () => {
        setModo("novo")
        setEmEdicao(null)
        setFormulario(formularioVazio)
        setPonto(pontoVazio)
        setResumo("")
        iniciarDialogo()
    }

    const abrirEdicao = (endereco: EnderecoCliente) => {
        const form = paraFormulario(endereco)
        setModo("editar")
        setEmEdicao(endereco)
        setFormulario(form)
        setPonto({ lat: endereco.latitude, lng: endereco.longitude })
        setResumo(resumoEndereco(form))
        iniciarDialogo()
    }

    const fecharDialogo = () => {
        setModo(null)
        setEmEdicao(null)
    }

    const escolherNoMapa = (place: EnderecoGoogle) => {
        setFormulario(prev => ({
            ...prev,
            logradouro: place.rua ?? "",
            // Clique no mapa nao traz numero: na edicao mantem o atual
            numero: place.numero || (modo === "editar" ? prev.numero : ""),
            bairro: place.bairro ?? "",
            cidade: place.cidade ?? "",
            uf: place.uf ?? "",
            cep: place.cep ?? "",
        }))
        setPonto({ lat: place.lat ?? null, lng: place.lng ?? null })
        setPontoAlterado(true)
        setResumo(place.formatted ?? "")
    }

    const ajustarPonto = (novo: { lat: number; lng: number }) => {
        setPonto(novo)
        setPontoAlterado(true)
    }

    const continuarParaDados = () => {
        setEtapa("dados")
        setDestacarNumero(!formulario.numero)
        setTimeout(() => {
            const id = formulario.numero ? "enderecoClienteComplemento" : "enderecoClienteNumero"
            document.getElementById(id)?.focus()
        }, 150)
    }

    const handleCampo =
        (campo: keyof FormularioEndereco) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const valor = e.target.value
            setFormulario(prev => ({ ...prev, [campo]: valor }))
            if (campo === "numero" && valor) setDestacarNumero(false)
        }

    const handleSalvarEndereco = async () => {
        if (!formulario.logradouro.trim()) {
            alertStart("O endereço é obrigatório.", "error")
            return
        }
        if (!formulario.numero.trim()) {
            alertStart("O número é obrigatório.", "error")
            setDestacarNumero(true)
            return
        }
        if (!formulario.bairro.trim()) {
            alertStart("O bairro é obrigatório.", "error")
            return
        }
        if (!formulario.cidade.trim()) {
            alertStart("A cidade é obrigatória.", "error")
            return
        }

        if (modo === "novo" && !formulario.uf.trim()) {
            alertStart("A UF é obrigatória.", "error")
            return
        }
        if (modo === "novo" && usarMapa && !ponto.lat) {
            alertStart("Busque o endereço ou marque o local no mapa.", "error")
            return
        }

        try {
            let mensagem: string
            if (modo === "novo") {
                mensagem = await adicionarEndereco({
                    logradouro: formulario.logradouro,
                    numero: formulario.numero,
                    complemento: formulario.complemento || null,
                    referencia: formulario.referencia || null,
                    bairro: formulario.bairro,
                    cidade: formulario.cidade,
                    uf: formulario.uf,
                    cep: formulario.cep || null,
                    latitude: ponto.lat,
                    longitude: ponto.lng,
                })
            } else {
                if (!emEdicao) return
                mensagem = await salvarEndereco(emEdicao.id, {
                    logradouro: formulario.logradouro,
                    numero: formulario.numero,
                    complemento: formulario.complemento || null,
                    referencia: formulario.referencia || null,
                    bairro: formulario.bairro,
                    cidade: formulario.cidade,
                    uf: formulario.uf || null,
                    cep: formulario.cep || null,
                    // So envia coordenadas quando o local foi escolhido/ajustado no mapa
                    ...(pontoAlterado && ponto.lat
                        ? { latitude: ponto.lat, longitude: ponto.lng }
                        : {}),
                })
            }
            alertStart(mensagem, "success")
            fecharDialogo()
        } catch (error) {
            const padrao =
                modo === "novo" ? "Erro ao cadastrar o endereço" : "Erro ao alterar o endereço"
            alertStart((error as Error).message || padrao, "error")
        }
    }

    const handleExcluirEndereco = async () => {
        if (!paraExcluir) return
        try {
            const mensagem = await excluirEndereco(paraExcluir.id)
            alertStart(mensagem, "success")
            setParaExcluir(null)
        } catch (error) {
            alertStart((error as Error).message || "Erro ao excluir o endereço", "error")
        }
    }

    return (
        <Paper className={classes.configCard}>
            <Box className={classes.cardHeader}>
                <Home className={classes.iconHeader} />
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    Endereços
                </Typography>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Add />}
                    onClick={abrirNovo}
                    style={{ marginLeft: "auto" }}
                >
                    Adicionar endereço
                </Button>
            </Box>

            <Box className={classes.cardContent}>
                <TableContainer style={{ maxHeight: 400, overflowX: "hidden" }}>
                    <Table
                        size="small"
                        stickyHeader
                        style={{ tableLayout: "fixed", width: "100%" }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: "8%" }}>Id</TableCell>
                                <TableCell style={{ width: "27%" }}>Logradouro</TableCell>
                                <TableCell style={{ width: "10%" }}>Número</TableCell>
                                <TableCell style={{ width: "17%" }}>Complemento</TableCell>
                                <TableCell style={{ width: "16%" }}>Bairro</TableCell>
                                <TableCell style={{ width: "12%" }}>Cidade</TableCell>
                                <TableCell style={{ width: "10%" }} align="center" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dados.enderecos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className={tabela.semResultado}>
                                        O cliente não possui endereços cadastrados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                dados.enderecos.map(endereco => (
                                    <TableRow key={endereco.id} hover>
                                        <TableCell>{endereco.id}</TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.logradouro ?? ""}
                                        >
                                            {endereco.logradouro || "-"}
                                        </TableCell>
                                        <TableCell>{endereco.numero || "-"}</TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.complemento ?? ""}
                                        >
                                            {endereco.complemento || "-"}
                                        </TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.bairro ?? ""}
                                        >
                                            {endereco.bairro || "-"}
                                        </TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.cidade ?? ""}
                                        >
                                            {endereco.cidade || "-"}
                                        </TableCell>
                                        <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => abrirEdicao(endereco)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setParaExcluir(endereco)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {endereco.latitude !== null &&
                                                endereco.longitude !== null && (
                                                    <Tooltip title="Ver no mapa">
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={`https://www.google.com.br/maps/place/${endereco.latitude},${endereco.longitude}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <Map fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog
                open={modo !== null}
                onClose={fecharDialogo}
                fullWidth
                maxWidth={etapa === "mapa" ? "md" : "sm"}
            >
                <DialogTitle>
                    {modo === "novo" ? "Novo endereço" : `Endereço ${emEdicao?.id ?? ""}`}
                </DialogTitle>
                <DialogContent>
                    {etapa === "mapa" ? (
                        <SelecaoEnderecoMapa
                            ponto={ponto}
                            resumo={resumo}
                            onEscolher={escolherNoMapa}
                            onAjustarPonto={ajustarPonto}
                            onBuscandoPonto={setBuscandoPonto}
                            centroLoja={centroLoja}
                            inputRef={buscaRef}
                            chaveBusca={chaveBusca}
                            alturaMapa={360}
                        />
                    ) : (
                        <>
                            {usarMapa ? (
                                <Paper
                                    variant="outlined"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "8px 12px",
                                        marginBottom: 16,
                                    }}
                                >
                                    <Room color="primary" />
                                    <Typography variant="body2" style={{ flex: 1 }}>
                                        {ponto.lat
                                            ? resumo || resumoEndereco(formulario)
                                            : "Sem localização no mapa"}
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="primary"
                                        startIcon={<EditLocation />}
                                        onClick={() => setEtapa("mapa")}
                                    >
                                        {ponto.lat ? "Alterar" : "Marcar no mapa"}
                                    </Button>
                                </Paper>
                            ) : null}
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Endereço"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required
                                        value={formulario.logradouro}
                                        onChange={handleCampo("logradouro")}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        id="enderecoClienteNumero"
                                        label="Número"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required
                                        error={destacarNumero}
                                        helperText={
                                            destacarNumero
                                                ? "Informe o número (ou S/N se não houver)"
                                                : undefined
                                        }
                                        value={formulario.numero}
                                        onChange={handleCampo("numero")}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        id="enderecoClienteComplemento"
                                        label="Complemento"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={formulario.complemento}
                                        onChange={handleCampo("complemento")}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Referência"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={formulario.referencia}
                                        onChange={handleCampo("referencia")}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <TextField
                                        label="Bairro"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required
                                        value={formulario.bairro}
                                        onChange={handleCampo("bairro")}
                                    />
                                </Grid>
                                <Grid item xs={8} sm={4}>
                                    <TextField
                                        label="Cidade"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required
                                        value={formulario.cidade}
                                        onChange={handleCampo("cidade")}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={3}>
                                    <TextField
                                        label="UF"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        required={modo === "novo"}
                                        inputProps={{ maxLength: 2 }}
                                        value={formulario.uf}
                                        onChange={handleCampo("uf")}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="CEP"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        inputProps={{ maxLength: 8 }}
                                        value={formulario.cep}
                                        onChange={handleCampo("cep")}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={fecharDialogo}>Cancelar</Button>
                    {etapa === "mapa" ? (
                        <>
                            {modo === "editar" ? (
                                <Button onClick={() => setEtapa("dados")}>
                                    Editar só os dados
                                </Button>
                            ) : null}
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!ponto.lat || buscandoPonto}
                                onClick={continuarParaDados}
                            >
                                Continuar
                            </Button>
                        </>
                    ) : (
                        <>
                            {usarMapa ? (
                                <Button onClick={() => setEtapa("mapa")}>Voltar</Button>
                            ) : null}
                            <Button
                                variant="contained"
                                className={classes.botaoSalvar}
                                onClick={handleSalvarEndereco}
                            >
                                {modo === "novo" ? "Cadastrar" : "Atualizar"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={paraExcluir !== null} onClose={() => setParaExcluir(null)}>
                <DialogTitle>Excluir endereço</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir o endereço {paraExcluir?.id} —{" "}
                        {paraExcluir?.logradouro}, {paraExcluir?.numero}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setParaExcluir(null)}>Cancelar</Button>
                    <Button color="secondary" variant="contained" onClick={handleExcluirEndereco}>
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
